import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer'
import { formatDateForUI, formatCurrency } from '@app/libs/formatters'

Font.registerHyphenationCallback((word) => [word])

const styles = StyleSheet.create({
  page: {
    padding: 20,
  },
  section: {
    marginBottom: 10,
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    fontFamily: 'Helvetica-Bold',
  },
  text: {
    fontFamily: 'Helvetica',
    fontSize: 12,
    marginBottom: 5,
  },
  cptCodes: {
    fontFamily: 'Helvetica',
    fontSize: 6,
    marginBottom: 5,
  },
  fieldText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12,
    marginBottom: 5,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  boldText: {
    fontFamily: 'Helvetica-Bold',
  },
  regularText: {
    fontFamily: 'Helvetica',
  },
  redText: {
    color: 'red',
  },
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    padding: 5,
  },
  tableHeader: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8, // Reduced font size for table headers
  },
  tableCell: {
    flex: 1,
    fontSize: 6,
    textAlign: 'center',
    wrap: true, // Ensure text wraps within the cell
  },
  claimTotalsCombinedCell: {
    flex: 3, // Combine the width of the first two cells
    fontSize: 6,
    textAlign: 'center',
    wrap: true,
  },
})

const lineItem = (claim) => {
  const { financials = {}, service_info = {} } = claim
  const { cptCodes = [], name, date_of_service_initial } = service_info
  const {
    original_charge = 0,
    allowed_amount = 0,
    ineligible_amount = 0,
    amount_paid = 0,
    deductible = 0,
    coinsurance = 0,
    copay = 0,
  } = financials
  return (
    <>
      <Text style={styles.tableCell}>{formatDateForUI(date_of_service_initial) || '---'}</Text>
      <Text style={styles.tableCell}>{name || '---'}</Text>
      <Text style={styles.tableCell}>
        {Array.isArray(cptCodes) ? (
          cptCodes.map((code, index) => (
            <Text key={index} style={styles.cptCodes}>
              {code}
              {index < cptCodes.length - 1 && ', '}
            </Text>
          ))
        ) : (
          <Text style={styles.cptCodes}>{cptCodes || '---'}</Text>
        )}
      </Text>
      <Text style={styles.tableCell}>---</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(original_charge - allowed_amount)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(allowed_amount || 0)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(ineligible_amount || 0)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(amount_paid || 0)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(deductible || 0)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(coinsurance || 0)}`}</Text>
      <Text style={styles.tableCell}>{`${formatCurrency(copay || 0)}`}</Text>
    </>
  )
}

const EmptyLineItem = () => (
  <>
    <Text style={styles.tableCell}>No claims available</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
    <Text style={styles.tableCell}>---</Text>
  </>
)

const buildLineItems = (claims) => {
  if (claims?.length > 0) {
    return claims.map((claim) => (
      <View key={claim.id} style={styles.tableRow}>
        {lineItem(claim)}
      </View>
    ))
  }
  return (
    <View style={styles.tableRow}>
      <EmptyLineItem />
    </View>
  )
}

const EOB_PDF = ({
  totals,
  patientInfo,
  mainProviderInfo,
  mainOrganizationInfo,
  planInfo,
  mainClaimInfo,
  claims = [],
}) => (
  <Document>
    <Page size='A4' style={styles.page}>
      <View style={styles.section}>
        {planInfo?.planLogo && (
          <Image
            src={planInfo.planLogo}
            style={{
              width: '100px',
              height: '100px',
              objectFit: 'contain',
              filter: 'grayscale(100%) brightness(0%)',
            }}
          />
        )}
        <Text style={styles.header}>Explanation of Benefits</Text>
      </View>
      <View style={styles.section}>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            Patient's Name:{' '}
            <Text
              style={{ ...styles.regularText, textTransform: 'capitalize' }}
            >{`${patientInfo?.firstname || ''} ${patientInfo?.lastname || ''}`}</Text>
          </Text>
          <Text style={styles.fieldText}>
            Claim Number:{' '}
            <Text style={styles.regularText}>{mainClaimInfo?.document_num || '---'}</Text>
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            Provider Name: <Text style={styles.regularText}>{mainProviderInfo?.name || '---'}</Text>
          </Text>
          <Text style={styles.fieldText}>
            Claim Processed Date:{' '}
            <Text style={styles.regularText}>
              {formatDateForUI(mainClaimInfo?.createdAt) || '---'}
            </Text>
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            Practice Name:{' '}
            <Text style={styles.regularText}>{mainOrganizationInfo?.name || '---'}</Text>
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            Plan Paid:{' '}
            <Text
              style={[styles.regularText, styles.redText]}
            >{`${formatCurrency(totals?.amount_paid || 0)}`}</Text>
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            Patient Paid:{' '}
            <Text
              style={[styles.regularText, styles.redText]}
            >{`${formatCurrency(totals?.patient_paid || 0)}`}</Text>
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.fieldText}>
            It is not your responsibility to pay:{' '}
            <Text
              style={[styles.regularText, styles.redText]}
            >{`${formatCurrency(totals?.original_charge - totals?.allowed_amount) || 0}`}</Text>
          </Text>
        </View>
      </View>
      <View style={styles.table}>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <Text style={styles.tableCell}>DATE OF SERVICE</Text>
          <Text style={styles.tableCell}>TREATMENT</Text>
          <Text style={styles.tableCell}>CPT</Text>
          <Text style={styles.tableCell}>BILLED</Text>
          <Text style={styles.tableCell}>SAVINGS</Text>
          <Text style={styles.tableCell}>CONTRACTED</Text>
          <Text style={styles.tableCell}>INELIGIBLE</Text>
          <Text style={styles.tableCell}>PLAN PAID</Text>
          <Text style={styles.tableCell}>DEDUC</Text>
          <Text style={styles.tableCell}>COINS</Text>
          <Text style={styles.tableCell}>COPAY</Text>
        </View>
        {buildLineItems(claims)}
        <View style={styles.tableRow}>
          <Text style={[styles.claimTotalsCombinedCell, styles.boldText]}>CLAIM TOTALS</Text>
          <Text style={styles.tableCell}>---</Text>
          <Text
            style={styles.tableCell}
          >{`${formatCurrency(totals?.original_charge - totals?.allowed_amount)}`}</Text>
          <Text style={styles.tableCell}>{`${formatCurrency(totals?.allowed_amount || 0)}`}</Text>
          <Text
            style={styles.tableCell}
          >{`${formatCurrency(totals?.ineligible_amount || 0)}`}</Text>
          <Text style={styles.tableCell}>{`${formatCurrency(totals?.amount_paid || 0)}`}</Text>
          <Text style={styles.tableCell}>{`${formatCurrency(totals?.deductible || 0)}`}</Text>
          <Text style={styles.tableCell}>{`${formatCurrency(totals?.coinsurance || 0)}`}</Text>
          <Text style={styles.tableCell}>{`${formatCurrency(totals?.copay || 0)}`}</Text>
        </View>
      </View>
    </Page>
  </Document>
)

export default EOB_PDF
